/**
 * SettingsDailyPlanningSection
 *
 * Daily planning wizard settings (morning wizard) and evening review settings.
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sun, Sunset } from 'lucide-react';
import type { CalendarSettings } from '../types';
import { Section, ToggleRow, Field, formatHour, HOURS } from './settings-helpers';

interface SettingsDailyPlanningSectionProps {
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
}

export function SettingsDailyPlanningSection({
  settings,
  onUpdateSettings,
}: SettingsDailyPlanningSectionProps) {
  const tf = settings.timeFormat ?? '12h';

  return (
    <>
      {/* Plan My Day */}
      <Section
        icon={Sun}
        title="Plan My Day"
        description="A morning wizard to review yesterday and set today's priorities."
      >
        <ToggleRow
          label="Enabled"
          checked={settings.dailyPlanningEnabled}
          onCheckedChange={(checked) => onUpdateSettings({ dailyPlanningEnabled: checked })}
        />
        <ToggleRow
          label="Review Yesterday"
          description="Show the review-yesterday step in the morning wizard."
          checked={settings.showReviewYesterday ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showReviewYesterday: checked })}
        />
        <ToggleRow
          label="Today's Schedule"
          description="Show today's schedule overview step."
          checked={settings.showTodaysSchedule ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showTodaysSchedule: checked })}
        />
        <ToggleRow
          label="Plan Your Day"
          description="Show the task-scheduling step."
          checked={settings.showPlanYourDay ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showPlanYourDay: checked })}
        />
        <ToggleRow
          label="Set Your Focus"
          description="Show the daily priorities step."
          checked={settings.showSetYourFocus ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showSetYourFocus: checked })}
        />

        <Field label="Wizard time window">
          <div className="flex items-center gap-2">
            <Select
              value={String(settings.morningWizardWindowStart ?? 6)}
              onValueChange={(v) => onUpdateSettings({ morningWizardWindowStart: parseInt(v, 10) })}
            >
              <SelectTrigger className="w-[6.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {formatHour(h, tf)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">to</span>
            <Select
              value={String(settings.morningWizardWindowEnd ?? 11)}
              onValueChange={(v) => onUpdateSettings({ morningWizardWindowEnd: parseInt(v, 10) })}
            >
              <SelectTrigger className="w-[6.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {formatHour(h, tf)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Field>
      </Section>

      {/* Evening Review */}
      <Section
        icon={Sunset}
        title="Evening Review"
        description="Reflect on your day, review completed tasks, and capture notes."
      >
        <ToggleRow
          label="Enabled"
          checked={settings.eveningReviewEnabled}
          onCheckedChange={(checked) => onUpdateSettings({ eveningReviewEnabled: checked })}
        />

        <Field label="Review time window">
          <div className="flex items-center gap-2">
            <Select
              value={String(settings.eveningReviewWindowStart ?? 16)}
              onValueChange={(v) => onUpdateSettings({ eveningReviewWindowStart: parseInt(v, 10) })}
            >
              <SelectTrigger className="w-[6.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {formatHour(h, tf)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">to</span>
            <Select
              value={String(settings.eveningReviewWindowEnd ?? 22)}
              onValueChange={(v) => onUpdateSettings({ eveningReviewWindowEnd: parseInt(v, 10) })}
            >
              <SelectTrigger className="w-[6.5rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>
                    {formatHour(h, tf)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Field>

        <ToggleRow
          label="Auto-trigger review"
          description="Automatically open the review during the time window."
          checked={settings.eveningReviewAutoTrigger ?? false}
          onCheckedChange={(checked) => onUpdateSettings({ eveningReviewAutoTrigger: checked })}
        />

        <ToggleRow
          label="Show day summary"
          checked={settings.showEveningDaySummary ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showEveningDaySummary: checked })}
        />

        <ToggleRow
          label="Show reschedule step"
          checked={settings.showEveningReschedule ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showEveningReschedule: checked })}
        />

        <ToggleRow
          label="Show reflection step"
          checked={settings.showEveningReflection ?? true}
          onCheckedChange={(checked) => onUpdateSettings({ showEveningReflection: checked })}
        />
      </Section>
    </>
  );
}
